import { useState } from "react";
import { Phone, Menu, X, Facebook, Instagram, Linkedin, Wrench } from "lucide-react";
import { getWhatsAppLink } from "@/lib/whatsapp";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);

  const navLinks = [
    { href: "#home", label: "Home" },
    { href: "#services", label: "AC Services" },
    { href: "#complaint-registration", label: "Complaint Registration" },
    { href: "#sale", label: "AC Sale" },
    { href: "#hvac-audit", label: "HVAC Audit" },
    { href: "#tonnage-calculator", label: "Tonnage Calculator" },
    { href: "#about", label: "About Us" },
    { href: "#why-us", label: "Why Choose Us" },
    { href: "#careers", label: "Careers" },
    { href: "#contact", label: "Contact" },
  ];

  const socialLinks = [
    { href: "https://www.facebook.com/profile.php?id=100086638418720", icon: Facebook, label: "Facebook" },
    { href: "https://www.instagram.com/marcohvacsurat/", icon: Instagram, label: "Instagram" },
    { href: "https://www.linkedin.com/company/90358068/admin/dashboard/", icon: Linkedin, label: "LinkedIn" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm animate-fade-in-down" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-2 hover:scale-105 transition-transform duration-300 flex-shrink-0" data-testid="logo-link">
            <img 
              src="/images/logo-marco.png" 
              alt="MARCO AIR CONDITION Logo"
              className="h-12 w-auto"
            />
            <span className="hidden md:block xl:hidden leading-tight" data-testid="brand-name-nav">
              <span className="block text-sm font-extrabold text-gray-900 tracking-wide">MARCO AIR CONDITION</span>
              <span className="block text-[10px] text-amber-600 font-semibold">Experience Comfort Cooling</span>
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center gap-2 2xl:gap-3">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-gray-700 hover:text-amber-600 font-semibold text-[13px] whitespace-nowrap transition-all duration-300 hover:scale-105 relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-amber-500 hover:after:w-full after:transition-all after:duration-300"
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Social Media & CTA */}
          <div className="hidden xl:flex items-center gap-2">
            {/* Social Icons */}
            <div className="hidden 2xl:flex items-center gap-1">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-amber-600 transition-all duration-300 hover:scale-125 p-1"
                  aria-label={social.label}
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
            
            <a href="tel:9825884460" className="flex items-center gap-1 text-amber-600 font-semibold text-sm hover:scale-105 transition-transform duration-300">
              <Phone className="h-4 w-4 animate-pulse" />
              9825884460
            </a>
            <a 
              href="#complaint-registration"
              className="animate-heartbeat inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold px-3 py-2 rounded-md transition-colors duration-300 hover:shadow-lg hover:shadow-red-500/40 text-sm whitespace-nowrap"
              data-testid="ac-emergency-btn"
            >
              <Wrench className="h-4 w-4" />
              AC Emergency Repair
            </a>
          </div>

          {/* Mobile: Emergency button + Menu toggle */}
          <div className="flex items-center gap-2 xl:hidden">
            <a
              href="#complaint-registration"
              className="animate-heartbeat inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white font-bold px-2.5 py-2 rounded-md text-[11px] leading-none whitespace-nowrap"
              data-testid="ac-emergency-btn-mobile"
            >
              <Wrench className="h-3.5 w-3.5" />
              AC Emergency Repair
            </a>
            <button
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="xl:hidden py-4 border-t animate-fade-in-down" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block py-3 text-gray-600 hover:text-amber-600 font-semibold transition-colors hover:pl-2"
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </a>
            ))}

            {/* Emergency button (full width) */}
            <a
              href="#complaint-registration"
              onClick={() => setIsOpen(false)}
              className="animate-heartbeat mt-2 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-md"
              data-testid="ac-emergency-btn-mobile-menu"
            >
              <Wrench className="h-5 w-5" />
              AC Emergency Repair
            </a>
            
            {/* Mobile Social Icons */}
            <div className="flex items-center gap-4 py-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-amber-600 transition-colors"
                  aria-label={social.label}
                >
                  <social.icon className="h-6 w-6" />
                </a>
              ))}
            </div>
            
            <a href="tel:9825884460" className="flex items-center gap-2 py-3 text-amber-600 font-semibold">
              <Phone className="h-5 w-5" />
              9825884460
            </a>
            <a 
              href={getWhatsAppLink("Hi! I would like to get a free quote for HVAC services.")}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 py-3 text-green-600 font-semibold"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp Us
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
